﻿/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import {FormProvider} from 'react-hook-form'
import {Lead} from 'types/entities/Lead'
import {Company} from 'types/entities/Company'
import {CompanyContact} from 'types/entities/CompanyContact'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import {useTranslation} from 'react-i18next'
import {useLeadBasicForm} from 'utils/hooks/useLeadBasicForm'
import LeadBasicFormLeadInfoSection from './LeadBasicFormLeadInfoSection'
import LeadBasicFormAccountSection from './LeadBasicFormAccountSection'
import LeadBasicFormContactSection from './LeadBasicFormContactSection'
import LeadBasicFormAddressSection from './LeadBasicFormAddressSection'

interface LeadBasicFormProps {
    onSuccess: (lead: Lead, shouldClose: boolean) => void
    onBack?: () => void
    compact?: boolean
    company?: Company | null
    disableCompanyChange?: boolean
    predefinedContact?: CompanyContact | null
    lead?: Lead
    highlightMissingFields?: boolean
    missingCompany?: boolean
    missingContact?: boolean
    onRequirementsChange?: (requirements: {hasCompany: boolean; hasContact: boolean}) => void
}

const LeadBasicForm = (props: LeadBasicFormProps) => {
    const {
        company,
        predefinedContact,
        compact = false,
        lead: propLead,
        disableCompanyChange = false,
        missingCompany = false,
        missingContact = false,
        onRequirementsChange,
        onSuccess,
    } = props
    const {t} = useTranslation()

    const {
        form,
        handleSubmit,
        onSubmit,
        onFormError,
        isSubmitting,
        isOpportunity,
        testField,
        applyOpportunityNameFromAnlasstyp,
        selectedCompany,
        companyTypeOptions,
        businessCategoryOptions,
        handleCompanySelect,
        handleCompanyNameChange,
        handleCreateCompanyFromLead,
        handleAutoSaveBlur,
        selectedCompanyContact,
        companyContacts,
        selectedContactCountry,
        setSelectedContactCountry,
        handleContactSelect,
        isNewContact,
        contactPhoneInput,
        effectiveLead,
        companyAddressOptions,
        selectedCompanyAddressId,
        hasExistingCompanyAddresses,
        isCreatingNewAddress,
        handleCompanyAddressSelect,
        handleCreateNewAddress,
        orderedCountryOptions,
        findCountryOption,
        selectedCountryCode,
        setSelectedCountryCode,
        countryInput,
        isSwissCountryInput,
        hasAddressField,
        tags,
        setTags,
        companyAutocompleteKey,
    } = useLeadBasicForm({
        lead: propLead,
        company,
        predefinedContact,
        onSuccess,
        onRequirementsChange,
    })

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit((data) => onSubmit(data, true), onFormError)}
                autoComplete="off"
                className={clsx(
                    'LeadBasicForm flex flex-col grow gap-y-4',
                    compact &&
                        '[&_input]:text-xs! [&_select]:text-xs! [&_textarea]:text-xs! [&_.ant-picker-input_input]:text-xs!'
                )}
            >
                <div className={clsx('grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch')}>
                    <LeadBasicFormLeadInfoSection
                        isOpportunity={isOpportunity}
                        opportunityStatus={effectiveLead?.status}
                        lead={effectiveLead}
                        compact={compact}
                        testField={testField}
                        tags={tags}
                        setTags={setTags}
                        onCreateOpportunityName={applyOpportunityNameFromAnlasstyp}
                    />

                    <LeadBasicFormAccountSection
                        compact={compact}
                        selectedCompany={selectedCompany}
                        companyAutocompleteKey={companyAutocompleteKey}
                        companyTypeOptions={companyTypeOptions}
                        businessCategoryOptions={businessCategoryOptions}
                        highlightMissingCompany={missingCompany}
                        disableCompanyChange={disableCompanyChange}
                        testField={testField}
                        handleCompanySelect={handleCompanySelect}
                        handleCompanyNameChange={handleCompanyNameChange}
                        handleCreateCompanyFromLead={handleCreateCompanyFromLead}
                        handleAutoSaveBlur={handleAutoSaveBlur}
                    />

                    <LeadBasicFormAddressSection
                        compact={compact}
                        hasExistingCompanyAddresses={hasExistingCompanyAddresses}
                        isCreatingNewAddress={isCreatingNewAddress}
                        companyAddressOptions={companyAddressOptions}
                        selectedCompanyAddressId={selectedCompanyAddressId}
                        selectedCountryCode={selectedCountryCode}
                        setSelectedCountryCode={setSelectedCountryCode}
                        orderedCountryOptions={orderedCountryOptions}
                        findCountryOption={findCountryOption}
                        isSwissCountryInput={isSwissCountryInput}
                        hasAddressField={hasAddressField}
                        highlightMissingContact={missingContact}
                        testField={testField}
                        countryInput={countryInput}
                        handleCompanyAddressSelect={handleCompanyAddressSelect}
                        handleCreateNewAddress={handleCreateNewAddress}
                    />

                    <LeadBasicFormContactSection
                        compact={compact}
                        selectedCompanyContact={selectedCompanyContact}
                        companyContacts={companyContacts}
                        highlightMissingContact={missingContact}
                        effectiveLead={effectiveLead}
                        isNewContact={isNewContact}
                        testField={testField}
                        selectedContactCountry={selectedContactCountry}
                        setSelectedContactCountry={setSelectedContactCountry}
                        contactPhoneInput={contactPhoneInput}
                        handleContactSelect={handleContactSelect}
                    />
                </div>

                <UiFormActionsBar
                    primaryText={isSubmitting ? t('Saving lead...') : t('Save')}
                    onPrimary={handleSubmit((data) => onSubmit(data, false), onFormError)}
                    saveAndCloseText={isSubmitting ? t('Saving lead...') : t('Save & Close')}
                    onSaveAndClose={handleSubmit((data) => onSubmit(data, true), onFormError)}
                    loading={isSubmitting}
                    backText={t('Back')}
                    onBack={props.onBack}
                />
            </form>
        </FormProvider>
    )
}

export default LeadBasicForm
